import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Player component handles video preview and thumbnail generation
 * 
 * Features:
 * - Video preview with controls
 * - Browser-native video processing
 * - Thumbnail generation using canvas
 * - Video metadata extraction
 */
const Player = ({ videoUrl, videoRef, onVideoLoaded, onGenerateThumbnails, processingRef }) => {
  const [isProcessingReady, setIsProcessingReady] = useState(true);
  const [isGeneratingThumbnails, setIsGeneratingThumbnails] = useState(false);
  const [processingError, setProcessingError] = useState(null);
  const canvasRef = useRef(null);

  /**
   * Initialize video processing capabilities
   * Uses browser-native APIs for fast and reliable processing
   */
  useEffect(() => {
    const initializeProcessing = async () => {
      try {
        console.log('Initializing video processing...');
        setProcessingError(null);
        
        // Browser-native processing is always ready
        const processing = { ready: true };
        
        console.log('Video processing initialized successfully!');
        processingRef.current = processing;
        setIsProcessingReady(true);
        
      } catch (error) {
        console.error('Failed to initialize video processing:', error);
        setProcessingError(error.message);
      }
    };

    initializeProcessing();
  }, [processingRef]);

  /**
   * Generate thumbnails for timeline navigation
   * Uses canvas to extract frames from the video
   */
  const generateThumbnails = useCallback(async (video, duration) => {
    if (!video || duration <= 0) return;

    setIsGeneratingThumbnails(true);
    const thumbnails = [];
    const thumbnailCount = Math.min(20, Math.floor(duration / 5)); // Max 20 thumbnails, one every 5 seconds
    const interval = duration / thumbnailCount;

    try {
      for (let i = 0; i < thumbnailCount; i++) {
        const time = i * interval;
        
        // Seek to the specific time
        video.currentTime = time;
        
        // Wait for seek to complete
        await new Promise((resolve) => {
          const handleSeeked = () => {
            video.removeEventListener('seeked', handleSeeked);
            resolve();
          };
          video.addEventListener('seeked', handleSeeked);
        });

        // Create canvas to capture frame
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext('2d');
          canvas.width = 160; // 16:9 aspect ratio
          canvas.height = 90;
          
          // Draw video frame to canvas
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          // Convert canvas to data URL
          const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.7);
          
          thumbnails.push({
            time: time,
            dataUrl: thumbnailDataUrl,
            timestamp: formatTime(time)
          });
        }
      }

      // Reset video to beginning
      video.currentTime = 0;
      
      onGenerateThumbnails(thumbnails);
    } catch (error) {
      console.error('Error generating thumbnails:', error);
    } finally {
      setIsGeneratingThumbnails(false);
    }
  }, [onGenerateThumbnails]);

  /**
   * Handle video metadata loaded
   * Extract duration and generate thumbnails
   */
  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      const duration = video.duration;
      onVideoLoaded(duration);
      
      // Generate thumbnails after a short delay to ensure video is ready
      setTimeout(() => {
        generateThumbnails(video, duration);
      }, 1000);
    }
  }, [onVideoLoaded, videoRef, generateThumbnails]);

  /**
   * Format time in MM:SS format
   */
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="player-container">
      <video
        ref={videoRef}
        src={videoUrl}
        controls
        className="video-player"
        onLoadedMetadata={handleLoadedMetadata}
        preload="metadata"
      >
        Your browser does not support the video tag.
      </video>
      
      {/* Hidden canvas for thumbnail generation */}
      <canvas
        ref={canvasRef}
        style={{ display: 'none' }}
      />
      
      {/* Video processing status */}
      {!isProcessingReady && !processingError && (
        <div className="info-message">
          <div>Initializing video processing engine...</div>
          <div style={{ marginTop: '10px', fontSize: '12px', color: '#7f8c8d' }}>
            ⚡ Browser-native processing loads instantly!
          </div>
        </div>
      )}
      
      {processingError && (
        <div className="error-message">
          <div>❌ {processingError}</div>
          <button 
            onClick={() => window.location.reload()} 
            style={{ 
              marginTop: '10px', 
              padding: '8px 16px', 
              background: '#e74c3c', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Refresh Page
          </button>
        </div>
      )}
      
      {isProcessingReady && (
        <div className="success-message">
          ⚡ Video processing engine ready! Using browser-native APIs for fast processing.
        </div>
      )}
      
      {/* Thumbnail generation status */}
      {isGeneratingThumbnails && (
        <div className="info-message">
          Generating thumbnails for timeline navigation...
        </div>
      )}
    </div>
  );
};

export default Player;